import moment from "moment-timezone";
import { DateType, DateFormat, getDateMoment } from "./dateHelper";

/** Preserve the existing field's occurrence when its destination clock repeats at DST end. */
export function getDestinationDateTime(
    value: DateType,
    previousValue: DateType,
    timeZone: string,
): moment.Moment {
    if (value === null) return moment.invalid();
    const clockFormats = [
        "YYYY-MM-DDTHH:mm:ss.SSS",
        "YYYY-MM-DDTHH:mm:ss",
        "YYYY-MM-DDTHH:mm",
        "YYYY-MM-DD",
        DateFormat.CLIENT_DATE_TIME,
        DateFormat.CLIENT_DATE,
    ];
    const clockString = typeof value === "string" && !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
    const parsed = clockString
        ? moment.tz(value, clockFormats, true, timeZone)
        : getDateMoment(value, { timeZone });
    if (
        clockString &&
        moment.utc(value, clockFormats, true).format("YYYY-MM-DDTHH:mm:ss.SSS") !==
            parsed.format("YYYY-MM-DDTHH:mm:ss.SSS")
    )
        return moment.invalid();
    if (!parsed.isValid()) return moment.invalid();
    const zone = moment.tz.zone(timeZone);
    if (!zone) return moment.invalid();
    const wall = parsed.format("YYYY-MM-DDTHH:mm:ss.SSS");
    const wallTimestamp = moment.utc(wall).valueOf();
    const candidates = [...new Set(zone.offsets)]
        .map(offset => moment(wallTimestamp + offset * 60 * 1000).tz(timeZone))
        .filter(candidate => candidate.format("YYYY-MM-DDTHH:mm:ss.SSS") === wall)
        .sort((left, right) => left.valueOf() - right.valueOf());
    const previous =
        previousValue === null ? moment.invalid() : getDateMoment(previousValue, { timeZone });
    return (
        candidates.find(
            candidate => previous.isValid() && candidate.utcOffset() === previous.utcOffset(),
        ) ??
        candidates[0] ??
        moment.invalid()
    );
}

/** Strict parsing includes a wall-component roundtrip: Moment otherwise normalizes DST gaps. */
export function parseDestinationClock(
    value: string,
    format: string,
    locale: string,
    timeZone: string,
) {
    const requested = moment.utc(value, format, locale, true);
    const parsed = moment.tz(value, format, locale, true, timeZone);
    return requested.isValid() &&
        parsed.isValid() &&
        requested.format("YYYY-MM-DDTHH:mm:ss.SSS") === parsed.format("YYYY-MM-DDTHH:mm:ss.SSS")
        ? parsed
        : moment.invalid();
}
